import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Shield, KeyRound, UserPlus, Trash2, RefreshCw, Users, Info, ShieldCheck } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import PasswordInput from "@/components/ui/password-input";
import api from "@/utils/api";

/** Passwort-Policy clientside (identisch zu Backend). */
const validatePolicy = (pw) => {
  if (!pw || pw.length < 12) return "Mindestens 12 Zeichen erforderlich";
  if (!/[A-Z]/.test(pw)) return "Mindestens ein Großbuchstabe";
  if (!/[a-z]/.test(pw)) return "Mindestens ein Kleinbuchstabe";
  if (!/\d/.test(pw)) return "Mindestens eine Zahl";
  if (!/[^A-Za-z0-9]/.test(pw)) return "Mindestens ein Sonderzeichen";
  return null;
};

const policyChecks = (pw) => ([
  { label: "Mindestens 12 Zeichen", ok: !!pw && pw.length >= 12 },
  { label: "Großbuchstabe", ok: /[A-Z]/.test(pw || "") },
  { label: "Kleinbuchstabe", ok: /[a-z]/.test(pw || "") },
  { label: "Zahl", ok: /\d/.test(pw || "") },
  { label: "Sonderzeichen", ok: /[^A-Za-z0-9]/.test(pw || "") },
]);

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

// ---------- 1) Passwort ändern ----------
const ChangePasswordSection = () => {
  const navigate = useNavigate();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const policyError = useMemo(() => validatePolicy(newPw), [newPw]);
  const mismatch = newPw && newPw2 && newPw !== newPw2;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!oldPw) return toast.error("Bitte aktuelles Passwort eingeben");
    if (policyError) return toast.error(policyError);
    if (newPw !== newPw2) return toast.error("Passwort-Wiederholung stimmt nicht überein");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Passwort geändert. Bitte erneut anmelden.");
      // Auto-Logout: Server widerruft alle Sessions (CRP-011)
      setTimeout(() => navigate("/admin/login"), 800);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Passwort konnte nicht geändert werden";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="section-change-password">
      <CardHeader>
        <div className="flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-[#4A331D]" />
          <CardTitle className="text-[#4A331D]">Passwort ändern</CardTitle>
        </div>
        <CardDescription>
          Nach erfolgreicher Änderung werden Sie automatisch abgemeldet und müssen sich erneut anmelden.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4 max-w-lg">
          <div>
            <Label htmlFor="old_pw">Aktuelles Passwort</Label>
            <PasswordInput id="old_pw" value={oldPw} onChange={(e) => setOldPw(e.target.value)}
              required autoComplete="current-password" className="mt-2"
              testId="change-pw-old" />
          </div>
          <div>
            <Label htmlFor="new_pw">Neues Passwort</Label>
            <PasswordInput id="new_pw" value={newPw} onChange={(e) => setNewPw(e.target.value)}
              required autoComplete="new-password" className="mt-2"
              testId="change-pw-new" />
            <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {policyChecks(newPw).map((c, i) => (
                <li key={i} className={c.ok ? "text-green-700" : "text-gray-500"}>
                  {c.ok ? "✓" : "○"} {c.label}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <Label htmlFor="new_pw2">Neues Passwort wiederholen</Label>
            <PasswordInput id="new_pw2" value={newPw2} onChange={(e) => setNewPw2(e.target.value)}
              required autoComplete="new-password" className="mt-2"
              testId="change-pw-confirm" />
            {mismatch && <p className="text-xs text-red-600 mt-1">Passwörter stimmen nicht überein</p>}
          </div>
          <Button type="submit" disabled={loading || !!policyError || !oldPw || newPw !== newPw2}
            className="bg-[#4A331D] hover:bg-[#593B23]"
            data-testid="change-pw-submit">
            {loading ? "Speichere…" : "Passwort ändern"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// ---------- 2) Benutzerverwaltung ----------
const UsersSection = ({ currentUsername }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create user form state
  const [showCreate, setShowCreate] = useState(false);
  const [cUsername, setCUsername] = useState("");
  const [cPw, setCPw] = useState("");
  const [cPw2, setCPw2] = useState("");
  const [cRole, setCRole] = useState("admin");
  const [creating, setCreating] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, username, role}

  // Reset password modal
  const [resetTarget, setResetTarget] = useState(null); // {id, username}
  const [rPw, setRPw] = useState("");
  const [rPw2, setRPw2] = useState("");
  const [resetting, setResetting] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data || []);
    } catch (e) {
      toast.error("Benutzer konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const adminCount = users.filter((u) => u.role === "admin").length;

  const onCreate = async (e) => {
    e.preventDefault();
    const pErr = validatePolicy(cPw);
    if (pErr) return toast.error(pErr);
    if (cPw !== cPw2) return toast.error("Passwort-Wiederholung stimmt nicht überein");
    setCreating(true);
    try {
      await api.post("/admin/users", { username: cUsername.trim(), password: cPw, role: cRole });
      toast.success(`Benutzer „${cUsername}" angelegt`);
      setCUsername(""); setCPw(""); setCPw2(""); setCRole("admin"); setShowCreate(false);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Benutzer konnte nicht angelegt werden");
    } finally {
      setCreating(false);
    }
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success(`Benutzer „${deleteTarget.username}" gelöscht`);
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const onResetSubmit = async (e) => {
    e.preventDefault();
    const pErr = validatePolicy(rPw);
    if (pErr) return toast.error(pErr);
    if (rPw !== rPw2) return toast.error("Passwort-Wiederholung stimmt nicht überein");
    setResetting(true);
    try {
      await api.patch(`/admin/users/${resetTarget.id}/password`, { new_password: rPw });
      toast.success(`Passwort von „${resetTarget.username}" zurückgesetzt`);
      setResetTarget(null); setRPw(""); setRPw2("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset fehlgeschlagen");
    } finally {
      setResetting(false);
    }
  };

  return (
    <Card data-testid="section-users">
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#4A331D]" />
            <CardTitle className="text-[#4A331D]">Benutzerverwaltung</CardTitle>
          </div>
          <CardDescription>Verwalten Sie Admin-Zugänge zum BEKAJ BAU Panel.</CardDescription>
        </div>
        <Button onClick={() => setShowCreate((s) => !s)}
          className="bg-[#4A331D] hover:bg-[#593B23]" data-testid="open-create-user">
          <UserPlus className="w-4 h-4 mr-2" />
          {showCreate ? "Schließen" : "Neuer Benutzer"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {showCreate && (
          <form onSubmit={onCreate} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3 max-w-2xl"
            data-testid="create-user-form">
            <h4 className="font-semibold text-[#4A331D]">Neuen Benutzer anlegen</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="c_user">Benutzername</Label>
                <Input id="c_user" value={cUsername} onChange={(e) => setCUsername(e.target.value)}
                  required minLength={3} maxLength={64} className="mt-2"
                  data-testid="create-user-username" autoComplete="off" />
              </div>
              <div>
                <Label htmlFor="c_role">Rolle</Label>
                <Select value={cRole} onValueChange={setCRole}>
                  <SelectTrigger id="c_role" className="mt-2" data-testid="create-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin (volle Rechte)</SelectItem>
                    <SelectItem value="editor" disabled>Editor (in Vorbereitung)</SelectItem>
                    <SelectItem value="viewer" disabled>Viewer (in Vorbereitung)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="c_pw">Passwort</Label>
                <PasswordInput id="c_pw" value={cPw} onChange={(e) => setCPw(e.target.value)}
                  required autoComplete="new-password" className="mt-2"
                  testId="create-user-pw" />
              </div>
              <div>
                <Label htmlFor="c_pw2">Passwort wiederholen</Label>
                <PasswordInput id="c_pw2" value={cPw2} onChange={(e) => setCPw2(e.target.value)}
                  required autoComplete="new-password" className="mt-2"
                  testId="create-user-pw2" />
              </div>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {policyChecks(cPw).map((c, i) => (
                <li key={i} className={c.ok ? "text-green-700" : "text-gray-500"}>
                  {c.ok ? "✓" : "○"} {c.label}
                </li>
              ))}
            </ul>
            <Button type="submit" disabled={creating} className="bg-[#4A331D] hover:bg-[#593B23]"
              data-testid="create-user-submit">
              {creating ? "Lege an…" : "Benutzer anlegen"}
            </Button>
          </form>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="text-left text-gray-600 border-b">
                <th className="py-2 pr-4">Benutzername</th>
                <th className="py-2 pr-4">Rolle</th>
                <th className="py-2 pr-4">Erstellt am</th>
                <th className="py-2 pr-4 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={4} className="py-4 text-gray-500">Lade Benutzer…</td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={4} className="py-4 text-gray-500">Keine Benutzer gefunden</td></tr>
              )}
              {!loading && users.map((u) => {
                const isSelf = u.username === currentUsername;
                const isLastAdmin = u.role === "admin" && adminCount <= 1;
                return (
                  <tr key={u.id} className="border-b last:border-b-0" data-testid={`user-row-${u.username}`}>
                    <td className="py-3 pr-4 font-medium text-[#4A331D]">
                      {u.username} {isSelf && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#D4AA6A]">(Sie)</span>}
                    </td>
                    <td className="py-3 pr-4">
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-xs font-medium capitalize">
                        {u.role || "admin"}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-gray-600">{formatDate(u.created_at)}</td>
                    <td className="py-3 pr-4">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm"
                          onClick={() => { setResetTarget({ id: u.id, username: u.username }); setRPw(""); setRPw2(""); }}
                          data-testid={`reset-pw-${u.username}`}>
                          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Passwort zurücksetzen
                        </Button>
                        <Button variant="outline" size="sm"
                          className="text-red-600 hover:bg-red-50 border-red-200"
                          disabled={isLastAdmin}
                          title={isLastAdmin ? "Der letzte Admin kann nicht gelöscht werden" : undefined}
                          onClick={() => setDeleteTarget({ id: u.id, username: u.username, role: u.role, isSelf })}
                          data-testid={`delete-user-${u.username}`}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Löschen
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.isSelf
                ? `Achtung: Sie sind dabei, Ihren eigenen Account „${deleteTarget?.username}" zu löschen. Das Backend blockiert diese Aktion, um Aussperrung zu verhindern.`
                : `Der Benutzer „${deleteTarget?.username}" wird unwiderruflich gelöscht. Diese Aktion wird im Audit-Log protokolliert.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-user-confirm">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Modal */}
      <AlertDialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <AlertDialogContent data-testid="reset-pw-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Passwort zurücksetzen</AlertDialogTitle>
            <AlertDialogDescription>
              Neues Passwort für „{resetTarget?.username}" festlegen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <form onSubmit={onResetSubmit} className="space-y-3">
            <div>
              <Label htmlFor="r_pw">Neues Passwort</Label>
              <PasswordInput id="r_pw" value={rPw} onChange={(e) => setRPw(e.target.value)}
                required autoComplete="new-password" className="mt-2"
                testId="reset-pw-new" />
            </div>
            <div>
              <Label htmlFor="r_pw2">Wiederholen</Label>
              <PasswordInput id="r_pw2" value={rPw2} onChange={(e) => setRPw2(e.target.value)}
                required autoComplete="new-password" className="mt-2"
                testId="reset-pw-confirm" />
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {policyChecks(rPw).map((c, i) => (
                <li key={i} className={c.ok ? "text-green-700" : "text-gray-500"}>
                  {c.ok ? "✓" : "○"} {c.label}
                </li>
              ))}
            </ul>
            <AlertDialogFooter>
              <AlertDialogCancel type="button" data-testid="reset-pw-cancel">Abbrechen</AlertDialogCancel>
              <Button type="submit" disabled={resetting}
                className="bg-[#4A331D] hover:bg-[#593B23]"
                data-testid="reset-pw-submit">
                {resetting ? "Speichere…" : "Zurücksetzen"}
              </Button>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

// ---------- 3) Sicherheitsinformationen ----------
const SecurityInfoSection = () => (
  <Card data-testid="section-security-info">
    <CardHeader>
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-[#4A331D]" />
        <CardTitle className="text-[#4A331D]">Sicherheitsinformationen</CardTitle>
      </div>
      <CardDescription>Aktive Schutzmaßnahmen im Überblick.</CardDescription>
    </CardHeader>
    <CardContent>
      <ul className="space-y-2 text-sm text-gray-700">
        <li className="flex gap-2"><Info className="w-4 h-4 mt-0.5 text-[#4A331D] shrink-0" /> Passwörter werden mit <strong>bcrypt</strong> gehasht. Klartext-Passwörter werden nicht gespeichert oder protokolliert.</li>
        <li className="flex gap-2"><Info className="w-4 h-4 mt-0.5 text-[#4A331D] shrink-0" /> Login-Token (JWT, HS256) sind 24 Stunden gültig.</li>
        <li className="flex gap-2"><Info className="w-4 h-4 mt-0.5 text-[#4A331D] shrink-0" /> Rate Limit: max. 10 Login-Versuche/Minute und 10 Passwort-Änderungen/Minute pro IP-Adresse.</li>
        <li className="flex gap-2"><Info className="w-4 h-4 mt-0.5 text-[#4A331D] shrink-0" /> Audit-Log protokolliert alle sicherheitsrelevanten Aktionen (Login erfolgreich/fehlgeschlagen, Passwort-Änderungen, Benutzer-Anlage/Löschung, Passwort-Resets).</li>
        <li className="flex gap-2"><Info className="w-4 h-4 mt-0.5 text-[#4A331D] shrink-0" /> Passwort-Richtlinie: ≥ 12 Zeichen, Groß-/Kleinbuchstabe, Zahl, Sonderzeichen.</li>
        <li className="flex gap-2"><Info className="w-4 h-4 mt-0.5 text-[#4A331D] shrink-0" /> DSGVO: Leads &amp; Chat-Sessions werden nach 90 Tagen automatisch anonymisiert.</li>
        <li className="flex gap-2"><Info className="w-4 h-4 mt-0.5 text-[#4A331D] shrink-0" /> CORS in Produktion auf <code>anfrage.bekajbau.de</code> beschränkt.</li>
      </ul>
    </CardContent>
  </Card>
);

const AdminSecurity = () => {
  const [currentUsername, setCurrentUsername] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then((r) => setCurrentUsername(r.data?.username || null)).catch(() => {});
  }, []);

  return (
    <AdminLayout title="Sicherheit & Benutzer">
      <div className="space-y-6" data-testid="admin-security-page">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Shield className="w-4 h-4" />
          Eingeloggt als <strong className="text-[#4A331D]">{currentUsername || "—"}</strong>
        </div>
        <ChangePasswordSection />
        <UsersSection currentUsername={currentUsername} />
        <SecurityInfoSection />
      </div>
    </AdminLayout>
  );
};

export default AdminSecurity;
